import React from "react";
import {
  createRestyleComponent,
  createVariant,
  ResponsiveValue,
  VariantProps,
} from "@shopify/restyle";
import { Theme } from "app/Theme/Theme";
import { GestureResponderEvent, Platform, Pressable } from "react-native";
import { Box } from "../../index";
import Loader from "app/components/common/Loader";
import { MainText } from "../../index";
import normalize from "app/utils/normalize";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";

const Button = createRestyleComponent<
  VariantProps<Theme, "buttonVariants"> & React.ComponentProps<typeof Box>,
  Theme
>([createVariant({ themeKey: "buttonVariants" })], Box);

type IMainButtonProps = {
  onPress: (event: GestureResponderEvent) => void;
  onPressInButton?: (event: GestureResponderEvent) => void;
  onPressOutButton?: (event: GestureResponderEvent) => void;
  onLongPressButton?: (event: GestureResponderEvent) => void;
  children?: React.ReactNode;
  round?: boolean;
  isDisabled?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  testID?: string;
  text: string;
  color?: ResponsiveValue<keyof Theme["colors"], Theme>;
  textColor?: ResponsiveValue<keyof Theme["colors"], Theme>;
  textVariant?: ResponsiveValue<keyof Theme["textVariants"], Theme>;
  textMargin?: ResponsiveValue<keyof Theme["spacing"], Theme>;
  height?: number;
  margin?: ResponsiveValue<keyof Theme["spacing"], Theme>;
  shadow?: boolean;
  isContentCenter?: boolean;
};

function MainStrokeButton({
  onPress = event => {},
  onPressInButton = event => {},
  onPressOutButton = event => {},
  onLongPressButton = event => {},
  leftIcon,
  rightIcon,
  isDisabled = false,
  isLoading = false,
  text,
  color = "brand.500",
  textColor = "neutral.white",
  textVariant = "l4-semibold",
  height = isWebDesktop ? normalize(56) : normalize(48),
  shadow = false,
  isContentCenter = false,
  textMargin = "null",
  margin = "s",
}: IMainButtonProps) {
  const pressedIn = (event: GestureResponderEvent) => {
    onPressInButton(event);
  };

  const pressedOut = (event: GestureResponderEvent) => {
    onPressOutButton(event);
  };
  // need to create a variant for button default

  const setOpacity = (pressed, hovered) => {
    if (pressed || isDisabled) {
      return 0.6;
    }
    if (hovered) {
      return 0.9;
    }
  };

  return (
    <Pressable
      disabled={isDisabled || isLoading}
      style={({ pressed, hovered }) => [
        {
          alignItems: "center",
          opacity: setOpacity(pressed, hovered),
          width: "100%",
        },
      ]}
      onPress={onPress}
      onPressIn={pressedIn}
      onPressOut={pressedOut}
      onLongPress={onLongPressButton}
    >
      <Button
        variant="strokeButton"
        alignSelf="center"
        flexDirection="row"
        alignItems="center"
        backgroundColor="neutral.white"
        margin={margin}
        width="100%"
        height={height}
        shadowColor={shadow ? "neutral.900" : "transparent"}
        shadowOpacity={shadow ? 0.1 : 0}
        shadowRadius={shadow ? 4 : 0}
        shadowOffset={{ width: 0, height: 2 }}
        elevation={1}
        zIndex={1}
        borderColor={isDisabled ? "neutral.350" : "brand.500"} // ne fonctionne pas
      >
        {isLoading ? (
          <Loader
            size={Platform.OS === "web" ? normalize(30) : normalize(20)}
          />
        ) : (
          <>
            {leftIcon}
            <Box
              width={Platform.OS === "web" ? "fit-content" : undefined}
              flex={isContentCenter ? undefined : 1}
              justifyContent="center"
              alignItems={leftIcon || rightIcon ? "flex-start" : "center"}
            >
              <MainText
                variant={textVariant}
                margin={textMargin}
                color={textColor}
              >
                {text}
              </MainText>
            </Box>
            {rightIcon}
          </>
        )}
      </Button>
    </Pressable>
  );
}
export { MainStrokeButton };
